import React, { useState } from 'react';
import { BotConfig } from '../types';

interface EmbedCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: BotConfig;
}

const EmbedCodeModal: React.FC<EmbedCodeModalProps> = ({ isOpen, onClose, config }) => {
  const [activeTab, setActiveTab] = useState<'script' | 'react' | 'demo'>('react');

  if (!isOpen) return null;

  // INTELLIGENT ORIGIN DETECTION
  const hostname = window.location.hostname;
  const PROD_URL = 'https://voiceapps.vercel.app';
  const isProd = hostname.endsWith('.vercel.app');
  const appOrigin = isProd ? window.location.origin : PROD_URL;

  const TEST_API_KEY = process.env.API_KEY || "AIzaSyD4tsec4mKzqodrBGqppCrD9YAc9iTinsE"; 
  
  const { previewUrl, ...embedConfig } = config;
  const configJson = JSON.stringify(embedConfig);

  // The Script Snippet
  const scriptSnippet = `<!-- Add this to your website <body> -->
<script>
(function() {
  var config = ${configJson};
  var apiKey = "${TEST_API_KEY}";
  var origin = "${appOrigin}"; 
  
  function init() {
    var iframe = document.createElement('iframe');
    iframe.id = 'gemini-voice-bot-iframe';
    iframe.src = origin + "/?mode=embed"; 
    
    iframe.style.position = 'fixed';
    iframe.style.bottom = '0px';
    iframe.style.right = '0px';
    iframe.style.width = '400px'; 
    iframe.style.height = '720px';
    iframe.style.border = 'none';
    iframe.style.backgroundColor = 'transparent';
    iframe.style.zIndex = '2147483647';
    iframe.style.colorScheme = 'normal';
    
    iframe.allow = "microphone *; autoplay *"; 
    iframe.setAttribute('allowtransparency', 'true');
    
    window.addEventListener('message', function(e) {
      if (!e.data) return;

      if (e.data.type === 'GEMINI_BOT_LOADED') {
         iframe.contentWindow.postMessage({
            type: 'GEMINI_BOT_CONFIG',
            config: config,
            apiKey: apiKey
         }, '*');
      }

      if (e.data.type === 'gemini-widget-resize') {
        if (e.data.isOpen) {
          if (window.innerWidth < 480) {
            iframe.style.width = '100%';
            iframe.style.height = '100%';
          } else {
            iframe.style.width = '400px';
            iframe.style.height = '720px';
          }
        } else {
          iframe.style.width = '160px';
          iframe.style.height = '160px';
        }
      }
    });
    
    document.body.appendChild(iframe);
  }

  if (document.body) { init(); } else { window.addEventListener('DOMContentLoaded', init); }
})();
</script>`;

  const demoSnippet = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Demo: ${config.name}</title>
<!-- Bot Demo Page v5 -->
<style>
  body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; background: #f0f0f0; }
  #preview-frame { 
    width: 100vw; 
    height: 100vh; 
    border: none; 
    position: fixed; 
    inset: 0;
    z-index: 0; 
    background: white;
  }
  .loading-msg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #888;
    font-family: sans-serif;
    z-index: -1;
  }
</style>
</head>
<body>
  <div class="loading-msg">Loading Preview Website...<br><span style="font-size:12px">(If blank, site may block iframe embedding)</span></div>
  <iframe id="preview-frame" src="${config.previewUrl || 'https://example.com'}"></iframe>
${scriptSnippet.replace('<!-- Add this to your website <body> -->', '')}
</body>
</html>`;

  // Helper to format string array for React snippet
  const sheetArrayString = config.googleSheets && config.googleSheets.length 
    ? `[\n      ${config.googleSheets.map(s => `'${s}'`).join(',\n      ')}\n    ]`
    : '[]';

  const reactSnippet = `import WidgetPreview from './components/WidgetPreview';
import { VoiceName } from './types';

function MyWebsite() {
  const botConfig = {
    name: '${config.name}',
    voice: VoiceName.${config.voice},
    systemInstruction: \`${config.systemInstruction.replace(/`/g, '\\`')}\`,
    themeColor: '${config.themeColor}',
    avatarUrl: '${config.avatarUrl}',
    webhookUrl: '${config.webhookUrl || ''}',
    googleSheets: ${sheetArrayString}
  };
  
  const apiKey = "${TEST_API_KEY}";

  return (
    <div>
       <WidgetPreview 
          config={botConfig} 
          apiKey={apiKey} 
          hidePreview={true} 
       />
    </div>
  );
}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Get Embed Code</h3>
            <p className="text-sm text-gray-500">Integrate the bot into your project.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('script')}
            className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'script' ? 'border-blue-600 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 bg-gray-50'
            }`}
          >
            HTML Script (Elementor/Wix)
          </button>
          <button
            onClick={() => setActiveTab('react')}
            className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'react' ? 'border-blue-600 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 bg-gray-50'
            }`}
          >
            React
          </button>
           <button
            onClick={() => setActiveTab('demo')}
            className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'demo' ? 'border-blue-600 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 bg-gray-50'
            }`}
          >
            Demo Page
          </button>
        </div>
        
        <div className="p-6 flex-grow overflow-auto flex flex-col min-h-0">
          <div className="relative group flex-grow min-h-0 flex flex-col">
            <div className="absolute top-3 right-3 z-10">
                <button 
                onClick={() => {
                    let text = reactSnippet;
                    if (activeTab === 'script') text = scriptSnippet;
                    if (activeTab === 'demo') text = demoSnippet;
                    navigator.clipboard.writeText(text);
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs shadow-md transition flex items-center gap-1"
                >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Code
                </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-auto border border-gray-700 h-64 md:h-96 w-full resize-none block">
              {activeTab === 'react' && reactSnippet}
              {activeTab === 'script' && scriptSnippet}
              {activeTab === 'demo' && demoSnippet}
            </pre>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition shadow-sm font-medium">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmbedCodeModal;